import get from 'lodash.get';

import { HarvestEvent } from 'services/harvestEvents/types';
import {
    VirtualizedTableColumns,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import { makeSelectTableColumnWidths } from 'services/columnWidths/selectors';
// @ts-ignore
import { selectCurrentSeasonIsComplete } from 'services/currentSeasonId/selectors';
import { selectHarvestEventsArray } from 'services/harvestEvents/selectors';
import { selectEntities } from 'services/entities/selectors';
import { selectGroupOptions } from 'services/groups/selectors';
import { selectVarietyOptions } from 'services/varieties/selectors';
import { selectHarvestPrintProperties } from 'services/harvestPrintProperties/selectors';
import {
    selectIsAdmin,
    selectIsManager,
    selectIsAssistantManager
} from 'services/usersMe/selectors';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import { getHarvestEventColorGradeOptions } from 'services/harvestEvents/utils';
import formatDropdownOptions from 'utils/format-dropdown-options';
import {
    FIELD_TYPE_DROPDOWN,
    FIELD_TYPE_DROPDOWN_MULTIPLE,
    FIELD_TYPE_DATE
} from 'components/VirtualizedTable/constants';

const harvestPrintPropertyDropdownParameters = {
    keyField: 'id',
    textField: ({ externalId, name }: VirtualizedTableItem): string =>
        `${externalId} (${name})`,
    orderByField: 'orderIndex',
    filterPredicate: { deletedAt: null }
};

const selectHarvestEventsTableColumns = createDeepEqualSelector(
    [
        makeSelectTableColumnWidths(),
        selectCurrentSeasonIsComplete,
        selectHarvestPrintProperties,
        selectVarietyOptions,
        selectGroupOptions
    ],
    (
        tableColumnWidths,
        currentSeasonIsCompleteState,
        harvestPrintProperties,
        varietyOptions,
        groupOptions
    ): VirtualizedTableColumns => {
        const {
            harvestBinSuitabilities,
            harvestGrowingMethods,
            harvestLocations,
            harvestPalletTypes,
            harvestStorageTypes,
            harvestSubmissionProfiles
        } = harvestPrintProperties;

        const disabled = currentSeasonIsCompleteState;

        const result: VirtualizedTableColumns = [
            {
                label: 'Pick #',
                value: 'pickTypeName',
                width: 144,
                queryStringFilterField: 'pickTypeName'
            },
            {
                label: 'Area',
                value: 'entityPathNames',
                width: 144
            },
            {
                label: 'Variety',
                value: 'entityDefaultVarietyId',
                width: 144,
                type: FIELD_TYPE_DROPDOWN,
                options: varietyOptions,
                queryStringFilterField: 'varieties'
            },
            {
                label: 'Groups',
                value: 'groupIds',
                width: 144,
                type: FIELD_TYPE_DROPDOWN_MULTIPLE,
                options: groupOptions,
                queryStringFilterField: 'groups'
            },
            {
                label: 'Start Date',
                value: 'quantitySchedule',
                width: 144,
                type: FIELD_TYPE_DATE
            },
            {
                label: 'Forecasted',
                value: 'forecastedQuantity',
                width: 144,
                hasFooterSummary: true,
                unit: 'Bins'
            },
            {
                label: 'Scheduled',
                value: 'scheduledQuantity',
                width: 144,
                hasFooterSummary: true,
                unit: 'Bins'
            },
            {
                label: 'Bin Suitability',
                value: 'harvestEvent.harvestBinSuitabilityId',
                selectable: !disabled,
                width: 144,
                type: FIELD_TYPE_DROPDOWN,
                validators: 'isRequired',
                options: formatDropdownOptions(
                    harvestBinSuitabilities,
                    harvestPrintPropertyDropdownParameters
                )
            },
            {
                label: 'Pallet Type',
                value: 'harvestEvent.harvestPalletTypeId',
                selectable: !disabled,
                width: 144,
                type: FIELD_TYPE_DROPDOWN,
                validators: 'isRequired',
                options: formatDropdownOptions(
                    harvestPalletTypes,
                    harvestPrintPropertyDropdownParameters
                )
            },
            {
                label: 'Proposed Storage',
                value: 'harvestEvent.harvestStorageTypeId',
                selectable: !disabled,
                width: 144,
                type: FIELD_TYPE_DROPDOWN,
                validators: 'isRequired',
                options: formatDropdownOptions(
                    harvestStorageTypes,
                    harvestPrintPropertyDropdownParameters
                )
            },
            {
                label: 'Submission Profile',
                value: 'harvestEvent.harvestSubmissionProfileId',
                selectable: !disabled,
                width: 144,
                type: FIELD_TYPE_DROPDOWN,
                initialValue: null,
                emptyValueToNull: true,
                clearable: true,
                options: formatDropdownOptions(
                    harvestSubmissionProfiles,
                    harvestPrintPropertyDropdownParameters
                )
            },
            {
                label: 'Growing Method',
                value: 'harvestEvent.harvestGrowingMethodId',
                selectable: !disabled,
                width: 144,
                type: FIELD_TYPE_DROPDOWN,
                validators: 'isRequired',
                options: formatDropdownOptions(
                    harvestGrowingMethods,
                    harvestPrintPropertyDropdownParameters
                )
            },
            {
                label: 'Destination',
                value: 'harvestEvent.harvestLocationId',
                selectable: !disabled,
                width: 144,
                type: FIELD_TYPE_DROPDOWN,
                validators: 'isRequired',
                options: formatDropdownOptions(
                    harvestLocations,
                    harvestPrintPropertyDropdownParameters
                )
            },
            {
                label: 'Proposed Colour',
                value: 'harvestEvent.colorGrade',
                selectable: !disabled,
                width: 144,
                type: FIELD_TYPE_DROPDOWN,
                validators: 'isRequired',
                options: getHarvestEventColorGradeOptions()
            }
        ];

        return result.map((column) => {
            const { width, value } = column;
            return {
                ...column,
                width: get(tableColumnWidths, value, width)
            };
        });
    }
);

const selectHarvestEventsArrayItems = createDeepEqualSelector(
    [selectHarvestEventsArray, selectEntities],
    (harvestEventsArray, entities) =>
        harvestEventsArray.map(
            (harvestEvent: HarvestEvent): VirtualizedTableItem => {
                const { entityId } = harvestEvent;
                const {
                    path = undefined,
                    pathNames = undefined,
                    defaultVarietyId = undefined
                } = get(entities, entityId) || {};
                const quantitySchedule = get(harvestEvent, [
                    'quantitySchedules',
                    0,
                    'date'
                ]);

                return {
                    ...harvestEvent,
                    quantitySchedule,
                    // Provide entityPath to be used in filtering
                    entityPath: path,
                    entityPathNames: pathNames,
                    entityDefaultVarietyId: defaultVarietyId,
                    key: harvestEvent.uuid
                };
            }
        )
);

const selectIsWritable = createDeepEqualSelector(
    [
        selectCurrentSeasonIsComplete,
        selectIsAdmin,
        selectIsManager,
        selectIsAssistantManager
    ],
    (
        isSeasonComplete: boolean,
        isAdmin: boolean,
        isManager: boolean,
        isAssistantManager: boolean
    ) => !isSeasonComplete && (isAdmin || isManager || isAssistantManager)
);

export {
    selectHarvestEventsTableColumns,
    selectHarvestEventsArrayItems,
    selectIsWritable
};
